import { useEffect, useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button, Alert, Card, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SuccessAlert from '../components/common/SuccessAlert';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import { SamplePanelCardHeader } from '../components/samples/SamplePanelCardHeader';
import AdminStorageViewToggle from '../components/samples/AdminStorageViewToggle';
import ExcelImportIssueTables from '../components/samples/ExcelImportIssueTables';
import ExcelImportDropzone from '../components/samples/ExcelImportDropzone';
import { useExcelImportFlow } from '../hooks/useExcelImportFlow';
import { useRedirectIfSampleLocalPreferred } from '../hooks/useRedirectIfSampleLocalPreferred';
import type { StorageRoom } from '../services/storageRoom.service';
import {
  buildStorageRoomSelectLabels,
  formatStorageRoomOptionLabel,
  formatStorageRoomSelectOption,
} from '../utils/storageRoomSearch';
import { SAMPLE_STORAGE_SETUP_HREF } from '../utils/sampleStorageSetupGate';

const EXCEL_STEPS = [
  {
    n: 1 as const,
    titleKey: 'excel_import.step1_title',
    navKey: 'excel_import.step1_nav',
    defaultTitle: 'Choose storage & download template',
    defaultNav: 'Storage · template',
  },
  {
    n: 2 as const,
    titleKey: 'excel_import.step2_title',
    navKey: 'excel_import.step2_nav',
    defaultTitle: 'Upload file',
    defaultNav: 'Upload',
  },
  {
    n: 3 as const,
    titleKey: 'excel_import.step3_title',
    navKey: 'excel_import.step3_nav',
    defaultTitle: 'Validate data',
    defaultNav: 'Validate',
  },
  {
    n: 4 as const,
    titleKey: 'excel_import.step4_title',
    navKey: 'excel_import.step4_nav',
    defaultTitle: 'Import complete',
    defaultNav: 'Done',
  },
];

type ExcelImportTipItem = { key: string; defaultValue: string };

const TEMPLATE_TIP_ITEMS: ExcelImportTipItem[] = [
  {
    key: 'excel_import.template_info_item1',
    defaultValue: 'Two sheets (new samples, usage history)',
  },
  {
    key: 'excel_import.template_info_item2',
    defaultValue: 'Dropdown lists are preconfigured',
  },
  {
    key: 'excel_import.template_info_item3',
    defaultValue: 'Includes sample rows',
  },
  {
    key: 'excel_import.template_info_item4',
    defaultValue: 'Duplicate checking is enabled',
  },
];

const UPLOAD_NOTE_ITEMS: ExcelImportTipItem[] = [
  {
    key: 'excel_import.notes_item1',
    defaultValue: 'Import stops if duplicates remain',
  },
  {
    key: 'excel_import.notes_item2',
    defaultValue: 'Rows missing required fields are skipped',
  },
  {
    key: 'excel_import.notes_item3',
    defaultValue: 'Maximum file size is 10 MB',
  },
];

function ExcelImportTipsDetails({
  titleKey,
  titleDefault,
  titleHintKey,
  titleHintDefault,
  items,
}: {
  titleKey: string;
  titleDefault: string;
  titleHintKey?: string;
  titleHintDefault?: string;
  items: ExcelImportTipItem[];
}) {
  const { t } = useTranslation();
  const title = t(titleKey, { defaultValue: titleDefault });
  const hint = titleHintKey
    ? t(titleHintKey, { defaultValue: titleHintDefault ?? title })
    : title;
  return (
    <details className="excel-import-template-tips-details">
      <summary title={hint}>{title}</summary>
      <ul className="small text-muted excel-import-template-tips">
        {items.map((item) => (
          <li key={item.key}>{t(item.key, { defaultValue: item.defaultValue })}</li>
        ))}
      </ul>
    </details>
  );
}

type StorageRoomFieldProps = {
  storageRooms: StorageRoom[];
  pending: boolean;
  error: boolean;
  selectedId: number | undefined;
  selectedLabel: string | undefined;
  onChange: (id: number | undefined) => void;
  selectRef?: RefObject<HTMLSelectElement | null>;
  /** 未選択を強調（枠）。操作前は invalid にしない */
  emphasized?: boolean;
  /** ゲート操作後の未選択（aria-invalid + パルス） */
  invalid?: boolean;
  describedBy?: string;
};

function ExcelStorageRoomField({
  storageRooms,
  pending,
  error,
  selectedId,
  selectedLabel,
  onChange,
  selectRef,
  emphasized = false,
  invalid = false,
  describedBy,
}: StorageRoomFieldProps) {
  const { t, i18n } = useTranslation();

  if (pending) {
    return (
      <Form.Text className="text-muted d-block">
        {t('common.loading', { defaultValue: 'Loading...' })}
      </Form.Text>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mb-0 py-2 small">
        {t('storage_room.load_failed', {
          defaultValue: 'Failed to load storage rooms.',
        })}
      </Alert>
    );
  }

  if (storageRooms.length === 0) {
    return (
      <div>
        <Alert variant="warning" className="mb-2">
          <i className="fas fa-exclamation-triangle me-1" aria-hidden="true" />
          {t('excel_import.no_storage_rooms', {
            defaultValue: 'No storage rooms. Create a storage room first.',
          })}
        </Alert>
        <Button as={Link as any} to={SAMPLE_STORAGE_SETUP_HREF} variant="outline-primary" size="sm">
          <i className="fas fa-warehouse me-1" aria-hidden="true" />
          {t('excel_import.go_to_storage_rooms', {
            defaultValue: 'Create storage rooms on the storage room page',
          })}
        </Button>
      </div>
    );
  }

  const selectLabels = buildStorageRoomSelectLabels(storageRooms, i18n.language);

  return (
    <Form.Select
      ref={selectRef}
      id="storageRoomSelect"
      name="storageRoomId"
      className={`text-truncate excel-import-storage-select-control${
        emphasized && !selectedId ? ' excel-import-storage-select-control--needs-choice' : ''
      }${invalid && !selectedId ? ' excel-import-storage-select-control--nudge' : ''}`}
      title={
        selectedLabel ||
        t('excel_import.select_storage_room', { defaultValue: 'Select storage room' })
      }
      value={selectedId || ''}
      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)}
      required
      aria-required="true"
      aria-invalid={invalid && !selectedId ? true : undefined}
      aria-describedby={describedBy}
    >
      <option value="">
        {t('excel_import.select_storage_placeholder', {
          defaultValue: '保管庫を選んでください',
        })}
      </option>
      {storageRooms.map((room) => {
        const label = formatStorageRoomSelectOption(room, selectLabels);
        return (
          <option key={room.id} value={room.id} title={label}>
            {label}
          </option>
        );
      })}
    </Form.Select>
  );
}

export default function ExcelImport() {
  const { t } = useTranslation();
  useRedirectIfSampleLocalPreferred();
  const storageSelectRef = useRef<HTMLSelectElement | null>(null);
  const flow = useExcelImportFlow(t);
  const {
    showAdminViewToggle,
    adminViewAll,
    setAdminViewAll,
    step,
    setStep,
    error,
    setError,
    success,
    setSuccess,
    dropActive,
    setDropActive,
    selectedFile,
    selectedStorageRoomId,
    setSelectedStorageRoomId,
    storageRooms,
    storageRoomsPending,
    storageRoomsError,
    downloadTemplateMutation,
    validateMutation,
    importMutation,
    exportDataMutation,
    acceptImportFile,
    resetImportFlow,
    handleDownloadTemplate,
    handleFileSelect,
    goToUploadStep,
    handleValidate,
    handleImport,
  } = flow;

  const [storageGateNudge, setStorageGateNudge] = useState(false);

  const selectedStorageRoom = storageRooms.find((r) => r.id === selectedStorageRoomId);
  const selectedStorageRoomLabel = selectedStorageRoom
    ? formatStorageRoomOptionLabel(selectedStorageRoom)
    : undefined;
  const hasStorageRoom = Boolean(selectedStorageRoomId);
  const storageGateHint = t('excel_import.select_storage_to_continue', {
    defaultValue: '先に保管庫を選んでください。',
  });
  const exportNeedsStorageHint = t('excel_import.export_needs_storage', {
    defaultValue: '保管庫出力には、上の保管庫選択が必要です。',
  });
  const exportSectionDesc = t('excel_import.export_section_desc', {
    defaultValue: '選んだ保管庫のサンプルを Excel でダウンロードします（選択出力とは別）。',
  });
  const downloadTemplateLabel = t('excel_import.download_excel_template_short', {
    defaultValue: 'テンプレートDL',
  });
  const downloadTemplateTitle = t('excel_import.download_excel_template', {
    defaultValue: 'テンプレートをダウンロード',
  });
  const uploadNextLabel = t('excel_import.have_file_upload_short', {
    defaultValue: 'アップロードへ',
  });
  const uploadNextTitle = t('excel_import.have_file_upload', {
    defaultValue: 'アップロードへ進む',
  });
  const exportDataLabel = t('excel_import.export_data_short', {
    defaultValue: '保管庫出力',
  });
  const exportDataTitle = t('excel_import.export_data', {
    defaultValue: 'この保管庫のサンプルをExcelで出力',
  });
  // 可視・aria は短名。title は操作の説明
  const selectiveExportShort = t('excel_import.selective_export_link_short', {
    defaultValue: t('excel_export.nav_short', { defaultValue: '選択出力' }),
  });
  const selectiveExportTitle = t('excel_import.selective_export_link_title', {
    defaultValue: t('excel_import.selective_export_link', {
      defaultValue: 'サンプルを選んで出力',
    }),
  });

  useEffect(() => {
    if (hasStorageRoom) setStorageGateNudge(false);
  }, [hasStorageRoom]);

  const focusStorageSelect = () => {
    const el = storageSelectRef.current;
    if (!el) return;
    el.focus();
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  /** disabled だとクリック不能なので、未選択時はフォーカス誘導する */
  const runWithStorageRoom = (action: () => void) => {
    if (!selectedStorageRoomId) {
      setStorageGateNudge(true);
      focusStorageSelect();
      return;
    }
    action();
  };

  const storageGatedClass = hasStorageRoom ? undefined : 'excel-import-action--needs-storage';

  return (
    <Container fluid className="mt-2 px-3 px-md-4" id="excel-import-page">
      <SampleManagerNavigation />
      {/* ナビ短名「入出力」と可視 h1 が縦を奪うため sr-only。ヘルプ／管理者は chrome 行 */}
      <SampleManagerPageHeading
        shortLabel={t('navigation.excel_import_short', { defaultValue: 'Excel' })}
        fullLabel={t('excel_import.title', { defaultValue: 'Excel' })}
        srOnly
      />

      {success ? <SuccessAlert message={success} onDismiss={() => setSuccess('')} /> : null}
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <h2 className="visually-hidden">
        {t('excel_import.import_section_title', { defaultValue: 'Import' })}
      </h2>

      {/* ヘルプ／管理者トグルをステップ同一行に置くと「5・6番目の丸」に見えるため分離 */}
      <div className="excel-import-chrome-row d-flex justify-content-end align-items-center gap-1 mb-1">
        <CollapsibleHelpSection
          compact
          titleKey="excel_import.how_to_use_title"
          descriptionKey="excel_import.how_to_use_desc"
          stepsKey={[
            'excel_import.how_to_use_step1',
            'excel_import.how_to_use_step2',
            'excel_import.how_to_use_step3',
            'excel_import.how_to_use_step4',
          ]}
          noteKey="excel_import.how_to_use_note"
          storageKey="excel_import"
        />
        {showAdminViewToggle ? (
          <AdminStorageViewToggle
            checked={adminViewAll}
            onChange={setAdminViewAll}
            inputId="excel-import-admin-view-all"
            compact
            className="flex-shrink-0"
          />
        ) : null}
      </div>
      <ol
        className="rs-step-indicator excel-import-steps excel-import-steps-row mb-2"
        aria-label={t('excel_import.import_steps_aria', {
          defaultValue: t('excel_import.import_section_title'),
        })}
      >
        {EXCEL_STEPS.map((s) => {
          const title = t(s.titleKey, { defaultValue: s.defaultTitle });
          const nav = t(s.navKey, { defaultValue: s.defaultNav });
          const stepLabel = `${s.n}. ${title}`;
          return (
            <li
              key={s.n}
              className={`rs-step-indicator__item${step > s.n ? ' rs-step-indicator__item--done' : ''}`}
              aria-current={step === s.n ? 'step' : undefined}
              aria-label={stepLabel}
              title={title}
            >
              <span className="rs-step-indicator__num" aria-hidden="true">
                {s.n}
              </span>
              <span className="rs-step-indicator__label" aria-hidden="true">
                {nav}
              </span>
            </li>
          );
        })}
      </ol>

      {step === 1 && (
        <>
          <Card className="border-0 shadow-sm sample-panel-card excel-import-prepare-card">
            <SamplePanelCardHeader
              icon="fa-file-excel"
              title={t('excel_import.step1_title', {
                defaultValue: 'Choose storage & download template',
              })}
            />
            <Card.Body className="py-3">
              {/* 見出しが手順を表す。本文は保管庫の意味＋ゲートのみ（重複ガイドを畳む） */}
              <Form.Group className="mb-3 excel-import-storage-select">
                <Form.Label
                  htmlFor="storageRoomSelect"
                  className="fw-semibold small mb-1"
                  title={t('excel_import.storage_room_help', {
                    defaultValue: '保管庫出力とテンプレート／取り込みの対象保管庫です',
                  })}
                >
                  {t('excel_import.select_storage_room', {
                    defaultValue: 'Select storage room',
                  })}
                  <span className="text-danger ms-1" aria-hidden="true">
                    *
                  </span>
                </Form.Label>
                <ExcelStorageRoomField
                  storageRooms={storageRooms}
                  pending={storageRoomsPending}
                  error={storageRoomsError}
                  selectedId={selectedStorageRoomId}
                  selectedLabel={selectedStorageRoomLabel}
                  onChange={setSelectedStorageRoomId}
                  selectRef={storageSelectRef}
                  emphasized={!hasStorageRoom}
                  invalid={storageGateNudge && !hasStorageRoom}
                  describedBy={
                    !hasStorageRoom && storageGateNudge
                      ? 'excel-import-storage-gate'
                      : undefined
                  }
                />
                {/* プレースホルダと重複するため常時表示しない。未選択で操作したときだけ誘導 */}
                {!hasStorageRoom && storageGateNudge ? (
                  <p
                    id="excel-import-storage-gate"
                    className="excel-import-gate excel-import-gate--nudge small mt-2 mb-0"
                    role="status"
                  >
                    {storageGateHint}
                  </p>
                ) : null}
              </Form.Group>

              <div className="excel-import-download-block">
                <div className="d-flex flex-column flex-sm-row flex-wrap gap-2 excel-import-primary-actions">
                  <Button
                    variant={hasStorageRoom ? 'primary' : 'outline-secondary'}
                    className={storageGatedClass}
                    onClick={() => runWithStorageRoom(() => handleDownloadTemplate('excel'))}
                    disabled={downloadTemplateMutation.isPending}
                    aria-disabled={
                      downloadTemplateMutation.isPending || !hasStorageRoom ? true : undefined
                    }
                    title={hasStorageRoom ? downloadTemplateTitle : storageGateHint}
                    aria-label={downloadTemplateLabel}
                  >
                    {downloadTemplateMutation.isPending ? (
                      t('common.downloading', { defaultValue: 'Downloading...' })
                    ) : (
                      <>
                        <i className="fas fa-download me-1" aria-hidden="true" />
                        {downloadTemplateLabel}
                      </>
                    )}
                  </Button>
                  <Button
                    variant={hasStorageRoom ? 'outline-primary' : 'outline-secondary'}
                    className={storageGatedClass}
                    onClick={() => runWithStorageRoom(goToUploadStep)}
                    aria-disabled={!hasStorageRoom ? true : undefined}
                    title={hasStorageRoom ? uploadNextTitle : storageGateHint}
                    aria-label={uploadNextLabel}
                  >
                    <i className="fas fa-arrow-right me-1" aria-hidden="true" />
                    {uploadNextLabel}
                  </Button>
                </div>
                <ExcelImportTipsDetails
                  titleKey="excel_import.template_info_title"
                  titleDefault="テンプレートについて"
                  titleHintKey="excel_import.template_info_title_hint"
                  titleHintDefault="テンプレートの内容と注意点を表示"
                  items={TEMPLATE_TIP_ITEMS}
                />
              </div>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm sample-panel-card excel-import-export-card mt-3">
            <SamplePanelCardHeader
              icon="fa-file-export"
              title={t('excel_import.export_section_title', {
                defaultValue: 'Export existing data',
              })}
            />
            <Card.Body className="py-2">
              <div className="d-flex flex-column flex-sm-row flex-wrap gap-2 align-items-sm-center">
                <Button
                  variant={hasStorageRoom ? 'outline-success' : 'outline-secondary'}
                  size="sm"
                  className={storageGatedClass}
                  title={
                    hasStorageRoom
                      ? exportDataTitle
                      : exportNeedsStorageHint
                  }
                  aria-label={exportDataLabel}
                  onClick={() =>
                    runWithStorageRoom(() => {
                      if (selectedStorageRoomId == null) return;
                      exportDataMutation.mutate(selectedStorageRoomId);
                    })
                  }
                  disabled={exportDataMutation.isPending || storageRooms.length === 0}
                  aria-disabled={
                    exportDataMutation.isPending ||
                    storageRooms.length === 0 ||
                    !hasStorageRoom
                      ? true
                      : undefined
                  }
                >
                  {exportDataMutation.isPending ? (
                    t('common.exporting', { defaultValue: 'Exporting...' })
                  ) : (
                    <>
                      <i className="fas fa-file-excel me-1" aria-hidden="true" />
                      {exportDataLabel}
                    </>
                  )}
                </Button>
                <Link
                  to="/excel-export"
                  className="btn btn-outline-secondary btn-sm excel-import-selective-export-link"
                  title={selectiveExportTitle}
                  aria-label={selectiveExportShort}
                >
                  <i className="fas fa-check-square me-1" aria-hidden="true" />
                  {selectiveExportShort}
                </Link>
              </div>
              {/* 未選択時の重複ゲートは手順1の status に任せる。選ばれたら対象を明示 */}
              {hasStorageRoom ? (
                <Form.Text className="d-block mt-1 text-muted">{exportSectionDesc}</Form.Text>
              ) : null}
            </Card.Body>
          </Card>
        </>
      )}

      {step === 2 && (
        <Card className="border-0 shadow-sm sample-panel-card">
          <SamplePanelCardHeader
            icon="fa-upload"
            title={t('excel_import.step2_title', { defaultValue: 'Upload file' })}
          />
          <Card.Body className="py-2">
            {/* 見出しが手順。本文はドロップゾーン＋操作＋折りたたみ注意のみ */}
            <ExcelImportDropzone
              dropActive={dropActive}
              onDropActiveChange={setDropActive}
              onFile={acceptImportFile}
              onInputChange={handleFileSelect}
            />

            {selectedFile ? (
              <Alert variant="info" className="mb-2 py-2">
                {t('excel_import.selected_file', {
                  defaultValue: 'Selected file: {{fileName}} ({{fileSize}} KB)',
                  fileName: selectedFile.name,
                  fileSize: (selectedFile.size / 1024).toFixed(2),
                })}
              </Alert>
            ) : null}

            <div className="d-flex flex-column flex-sm-row flex-wrap gap-2">
              <Button
                variant="success"
                onClick={handleValidate}
                disabled={!selectedFile || validateMutation.isPending}
                aria-label={
                  validateMutation.isPending
                    ? t('excel_import.validating', { defaultValue: 'Validating...' })
                    : t('excel_import.validate_file_short', {
                        defaultValue: t('excel_import.validate_file'),
                      })
                }
                title={
                  !selectedFile
                    ? t('excel_import.validate_needs_file', {
                        defaultValue: '先にファイルを選んでください',
                      })
                    : t('excel_import.validate_file_title', {
                        defaultValue: t('excel_import.validate_file'),
                      })
                }
              >
                {validateMutation.isPending
                  ? t('excel_import.validating', { defaultValue: 'Validating...' })
                  : t('excel_import.validate_file', { defaultValue: 'Validate file' })}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setStep(1)}
                aria-label={t('common.back', { defaultValue: 'Back' })}
                title={t('excel_import.back_to_step1_title', {
                  defaultValue: '保管庫とテンプレートに戻る',
                })}
              >
                {t('common.back', { defaultValue: 'Back' })}
              </Button>
            </div>

            <ExcelImportTipsDetails
              titleKey="excel_import.notes_title"
              titleDefault="注意事項"
              titleHintKey="excel_import.notes_title_hint"
              titleHintDefault="インポート時の注意事項を表示"
              items={UPLOAD_NOTE_ITEMS}
            />

            {validateMutation.data?.data &&
            ((validateMutation.data.data.errors?.length ?? 0) > 0 ||
              (validateMutation.data.data.duplicates?.length ?? 0) > 0) ? (
              <div className="mt-3">
                <Alert variant="danger" className="py-2">
                  {t('excel_import.validation_errors', {
                    defaultValue:
                      'Validation: {{errorCount}} error(s), {{duplicateCount}} duplicate(s) found',
                    errorCount: validateMutation.data.data.errors?.length ?? 0,
                    duplicateCount: validateMutation.data.data.duplicates?.length ?? 0,
                  })}
                  <br />
                  {t('excel_import.how_to_use_note', {
                    defaultValue:
                      'Exact duplicate rows cannot be imported. Validation shows which rows conflict. Fix duplicates and validate again.',
                  })}
                </Alert>
                <ExcelImportIssueTables
                  errors={validateMutation.data.data.errors}
                  duplicates={validateMutation.data.data.duplicates}
                />
              </div>
            ) : null}
          </Card.Body>
        </Card>
      )}

      {step === 3 && (
        <Card className="border-0 shadow-sm sample-panel-card">
          <SamplePanelCardHeader
            icon="fa-check-double"
            title={t('excel_import.step3_header', { defaultValue: 'Step 3: Validation' })}
          />
          <Card.Body>
            {validateMutation.data ? (
              <Alert variant="success" className="mb-3">
                {t('excel_import.validation_complete', {
                  defaultValue: 'Validation finished with no errors.',
                })}
                <br />
                {t('excel_import.total_rows', {
                  defaultValue: 'Total rows: {{count}}',
                  count: validateMutation.data.data.total,
                })}
              </Alert>
            ) : null}

            <div className="d-grid gap-2 d-sm-flex">
              <Button
                variant="primary"
                className="flex-sm-fill"
                onClick={handleImport}
                disabled={importMutation.isPending}
              >
                {importMutation.isPending
                  ? t('excel_import.importing', { defaultValue: 'Importing...' })
                  : t('excel_import.execute_import', { defaultValue: 'Run import' })}
              </Button>
              <Button variant="secondary" className="flex-sm-fill" onClick={() => setStep(2)}>
                {t('common.back', { defaultValue: 'Back' })}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {step === 4 && (
        <Card className="border-0 shadow-sm sample-panel-card">
          <SamplePanelCardHeader
            icon="fa-flag-checkered"
            title={t('excel_import.step4_header', { defaultValue: 'Step 4: Import complete' })}
          />
          <Card.Body>
            {importMutation.data ? (
              <div className="mb-3">
                <Alert variant="success">
                  {importMutation.data.message}
                  <br />
                  {t('excel_import.total_rows', {
                    defaultValue: 'Total rows: {{count}}',
                    count: importMutation.data.data.total,
                  })}
                  <br />
                  {t('excel_import.imported_count', {
                    defaultValue: 'Imported: {{count}}',
                    count: importMutation.data.data.imported,
                  })}
                  {importMutation.data.data.errors.length > 0 ? (
                    <>
                      <br />
                      {t('excel_import.error_count', {
                        defaultValue: 'Errors: {{count}}',
                        count: importMutation.data.data.errors.length,
                      })}
                    </>
                  ) : null}
                </Alert>
                <ExcelImportIssueTables errors={importMutation.data.data.errors} />
              </div>
            ) : null}

            <div className="d-grid">
              <Button variant="primary" onClick={resetImportFlow}>
                {t('excel_import.start_new_import', { defaultValue: 'Start a new import' })}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
